import { useRefImageSource } from '../lib/refImageSource';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import type { Script } from '../../shared/types';
import { isBriefTemplate } from '../../shared/briefFields';
import {
  extractInterviewQuestions,
  extractWarmups,
  extractShootFields,
} from '../../shared/extractInterviewQuestions';
import { extractScriptRows, shootFamily } from '../../shared/scriptRows';
import Wordmark from '../components/Wordmark';
import Button from '../components/Button';
import InterviewQuestionCard from '../components/InterviewQuestionCard';
import WaveformIndicator from '../components/WaveformIndicator';
import ShootTabs from '../components/Shoot/ShootTabs';
import ShootBriefTab from '../components/Shoot/ShootBriefTab';
import ShootScriptTab from '../components/Shoot/ShootScriptTab';
import { shootableRows } from '../components/Shoot/useScriptFilter';
import { useShootTab, type ShootTab } from '../components/Shoot/useShootTab';
import WarmupBlock from '../components/Shoot/WarmupBlock';
import { useSwipeTabs } from '../components/Shoot/useSwipeTabs';
import { downloadInterviewPdf } from '../lib/downloadPdf';
import { useRecorder } from '../hooks/useRecorder';
import { useCallsheetEmbed, useCallsheetRecordingSignal } from '../lib/callsheetEmbed';

/**
 * Phone-friendly read-only view of just the Interview questions section.
 * Designed for shoot-day reference: large legible text, minimal chrome,
 * no comments or sidebar. Admin-only (gated by AdminGuard at the route).
 *
 * The page intentionally pulls a fresh script load on mount so it always
 * reflects the latest doc; on a phone there's no editor instance to mirror
 * unsaved keystrokes from, so we accept the "last autosave" cadence.
 *
 * Phase 2A — recording: the page now also drives the on-set audio
 * recorder. The interviewer taps Record, walks through the questions
 * tapping "Next question" between each, then taps Stop to upload the
 * take. Recording state lives in `useRecorder`; the upload + recordings
 * list live further up in the admin tree.
 *
 * Scripts (every template but the interview brief) open in Script mode: the
 * rows as reading lines with To camera / VO filters, and no recorder. A
 * script that also has a legacy "Interview questions" section gets Script and
 * Questions tabs; one with only the questions renders as it always has.
 *
 * `?embed=callsheet` (see lib/callsheetEmbed.ts) drops the header so the call
 * sheet's own bar can sit above the frame, and tells the call sheet while a
 * take is in progress so it can guard closing.
 */
export default function AdminInterviewView() {
  const { id } = useParams<{ id: string }>();
  useRefImageSource(id ? { kind: 'admin', scriptId: id } : null);
  const embedded = useCallsheetEmbed();
  const [script, setScript] = useState<Script | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  // Surface PDF render failures inline so a silent failure can't go
  // unnoticed on a phone where DevTools aren't easy to open.
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // ---- Recording state ----
  const recorder = useRecorder();
  const [activeQuestion, setActiveQuestion] = useState(0);
  // Take counter: starts at 1, increments after each successful upload.
  // Persists for the duration of the page session so consecutive takes
  // feel like one workflow even though they're separate uploads.
  const [takeNumber, setTakeNumber] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [lastUploadId, setLastUploadId] = useState<string | null>(null);
  // Scroll the active question card into view when it changes during a
  // take. Avoids the interviewer hunting on a long question list.
  const cardRefs = useRef<Array<HTMLLIElement | null>>([]);

  // ---- Tabs (briefs, and scripts that also carry questions) ----
  // Everything here has to be computed above the early returns below, so the
  // doc is read through an empty string until the script lands.
  const headerRef = useRef<HTMLElement>(null);
  const docJson = script?.doc_json ?? '';
  const isScript = script ? shootFamily(script.template_key) === 'script' : false;
  const questions = useMemo(() => extractInterviewQuestions(docJson), [docJson]);
  const warmups = useMemo(() => extractWarmups(docJson) ?? [], [docJson]);
  const shootFields = useMemo(() => extractShootFields(docJson), [docJson]);
  const rows = useMemo(
    () => (isScript ? shootableRows(extractScriptRows(docJson)) : []),
    [docJson, isScript],
  );
  const hasRows = rows.length > 0;
  const hasQuestions = questions !== null && questions.length > 0;
  // The left-hand tab: a brief's chosen fields, or a script's rows when the
  // script has questions too. Rows alone need no tabs.
  const lead = isScript ? 'script' : 'brief';
  const hasFields = isScript ? hasRows && hasQuestions : shootFields.length > 0;
  const scriptOnly = hasRows && !hasQuestions;
  // No recorder in Script mode (rows present). Briefs, and legacy scripts
  // that are only questions, keep it exactly as before.
  const recorderOn = hasQuestions && !hasRows;
  const [tab, setTab] = useShootTab(script?.slug ?? '', hasFields, hasQuestions, lead);
  // A user's tab change (strip or swipe) starts at the top of the new tab;
  // Record's forced switch goes straight to setTab so the active question
  // scroll still wins.
  const changeTab = useCallback((next: ShootTab) => {
    setTab(next);
    window.scrollTo({ top: 0 });
  }, [setTab]);
  const swipe = useSwipeTabs(tab, changeTab, lead);
  const panel: ShootTab = scriptOnly ? 'script' : tab;
  // In the call-sheet frame the header is gone, so the strips park at the top.
  const stickyUnder = embedded ? undefined : headerRef;

  // The call sheet guards its close button for the whole take: recording,
  // stopping and the upload after it.
  useCallsheetRecordingSignal(
    embedded,
    recorder.state === 'recording' || recorder.state === 'stopping' || uploading,
  );

  useEffect(() => {
    if (!id) return;
    adminApi
      .getScript(id)
      .then(setScript)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load.'));
  }, [id]);

  // Record reveals the mounted Questions panel. Wait for that render before
  // scrolling so the active card is visible and has a measurable position.
  useEffect(() => {
    if (recorder.state !== 'recording' || tab !== 'questions') return;
    const el = cardRefs.current[activeQuestion];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeQuestion, recorder.state, tab]);

  // Warn before navigating away mid-recording — losing a take to an
  // accidental swipe-back would be miserable on shoot day.
  useEffect(() => {
    if (recorder.state !== 'recording' && !uploading) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [recorder.state, uploading]);

  async function handleDownload() {
    if (!script || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    // One banner serves both; a leftover mic error would mask this one.
    recorder.clearError();
    setRecordError(null);
    try {
      await downloadInterviewPdf({
        title: script.title,
        client_name: script.client_name,
        slug: script.slug,
        doc_json: script.doc_json,
      });
    } catch (err) {
      console.error('Interview PDF download failed:', err);
      setDownloadError(
        err instanceof Error ? err.message : 'PDF download failed.',
      );
    } finally {
      setDownloading(false);
    }
  }

  async function handleStartRecording() {
    setRecordError(null);
    setLastUploadId(null);
    setActiveQuestion(0);
    // A take always happens against the questions, whichever tab you were
    // reading a second ago.
    setTab('questions', { remember: false });
    const result = await recorder.start();
    if (!result.ok) setRecordError(result.error ?? 'Could not start recording.');
  }

  async function handleStopRecording() {
    if (!script) return;
    const result = await recorder.stop();
    if (!result || result.blob.size === 0) {
      setRecordError('No audio was captured. Check microphone access and try again.');
      return;
    }
    setUploading(true);
    try {
      const res = await adminApi.uploadRecording(script.id, {
        audio: result.blob,
        mime_type: result.mimeType,
        duration_ms: result.durationMs,
        question_markers: result.markers,
      });
      setLastUploadId(res.id);
      setTakeNumber((n) => n + 1);
    } catch (err) {
      console.error('Recording upload failed:', err);
      setRecordError(
        err instanceof Error ? err.message : 'Upload failed.',
      );
    } finally {
      setUploading(false);
    }
  }

  function handleNextQuestion() {
    if (!questions) return;
    const next = Math.min(activeQuestion + 1, questions.length - 1);
    if (next === activeQuestion) return;
    setActiveQuestion(next);
    recorder.markQuestion(next);
  }

  function handleJumpToQuestion(index: number) {
    if (recorder.state !== 'recording') return;
    setActiveQuestion(index);
    recorder.markQuestion(index);
  }

  if (error) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <p className="text-error">{error}</p>
      </main>
    );
  }

  if (!script) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-faint font-mono text-[12px] uppercase tracking-[0.12em]">Loading…</p>
      </main>
    );
  }

  const isRecording = recorder.state === 'recording';
  const isStarting = recorder.state === 'requesting';
  const isStopping = recorder.state === 'stopping';
  const hasLastQuestion =
    questions !== null && activeQuestion >= questions.length - 1;

  return (
    <main className={`min-h-screen bg-page text-fg ${recorderOn ? 'pb-32' : 'pb-12'}`}>
      {!embedded && (
      <header
        ref={headerRef}
        className="border-b border-line bg-page/90 backdrop-blur sticky top-0 z-20"
      >
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <Link
            to={`/admin/scripts/${script.id}`}
            className="flex items-center gap-2 text-muted hover:text-fg transition-colors"
            // Visually disable the back link mid-recording so a thumb
            // brushing it doesn't kill the take. The beforeunload handler
            // is the real guard; this is just a clarity affordance.
            onClick={(e) => {
              if (isRecording) {
                e.preventDefault();
                setRecordError(
                  'Stop recording before navigating away.',
                );
              }
            }}
          >
            <span aria-hidden>←</span>
            <Wordmark size="sm" />
          </Link>
          <div className="flex items-center gap-3">
            {isRecording ? (
              <RecordingTimer
                elapsedMs={recorder.elapsedMs}
                takeNumber={takeNumber}
                warnSoftly={recorder.warnSoftly}
                warnUrgently={recorder.warnUrgently}
                audioLevels={recorder.audioLevels}
              />
            ) : (
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                Shoot view
              </span>
            )}
            {hasQuestions && !isRecording && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                disabled={downloading || uploading}
                title="Download a printable / offline PDF for shoot day"
              >
                {downloading ? 'Preparing…' : 'PDF'}
              </Button>
            )}
          </div>
        </div>
        {(recorder.warnSoftly || recorder.warnUrgently) && isRecording && (
          <CountdownBanner
            warnUrgently={recorder.warnUrgently}
            elapsedMs={recorder.elapsedMs}
          />
        )}
      </header>
      )}

      <section className="max-w-2xl mx-auto px-5 py-8 md:py-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint mb-2">
          // {script.client_name}
        </p>
        <h1 className="font-display font-bold text-[28px] md:text-[36px] leading-[1.1] tracking-[-0.02em] mb-2">
          {script.title}
        </h1>
        <p className={`text-muted text-[14px] ${hasFields || scriptOnly ? 'mb-3' : 'mb-10'}`}>
          {isScript && (hasRows || !hasQuestions)
            ? 'Script'
            : hasFields
              ? 'Interview brief'
              : 'Interview questions'}
        </p>

        {hasFields && (
          <ShootTabs
            active={tab}
            onChange={changeTab}
            lead={lead}
            leadCount={isScript ? rows.length : shootFields.length}
            questionCount={questions?.length ?? 0}
            stickyUnder={stickyUnder}
            stickyAtTop={embedded}
          />
        )}

        <div
          className={hasFields ? 'mt-5' : undefined}
          onTouchStart={hasFields ? swipe.onTouchStart : undefined}
          onTouchEnd={hasFields ? swipe.onTouchEnd : undefined}
        >
          {isScript ? (
            <div
              id="shoot-panel-script"
              role={hasFields ? 'tabpanel' : undefined}
              aria-labelledby={hasFields ? 'shoot-tab-script' : undefined}
              hidden={panel !== 'script'}
            >
              {hasRows && (
                <ShootScriptTab
                  slug={script.slug}
                  rows={rows}
                  stickyUnder={scriptOnly ? stickyUnder : undefined}
                  stickyAtTop={scriptOnly && embedded}
                />
              )}
            </div>
          ) : (
            <div
              id="shoot-panel-brief"
              role="tabpanel"
              aria-labelledby="shoot-tab-brief"
              hidden={tab !== 'brief'}
            >
              {hasFields && <ShootBriefTab docJson={script.doc_json} audienceLabels />}
            </div>
          )}
          <div
            id="shoot-panel-questions"
            role={hasFields ? 'tabpanel' : undefined}
            aria-labelledby={hasFields ? 'shoot-tab-questions' : undefined}
            hidden={panel !== 'questions'}
          >
            <WarmupBlock key={script.slug} slug={script.slug} questions={warmups} />
            {!questions || questions.length === 0 ? (
              <div className="border border-dashed border-line rounded-md px-6 py-12 text-center bg-card">
                <p className="text-muted">
                  {isBriefTemplate(script.template_key)
                    ? 'No Interview questions field is switched on for this brief.'
                    : 'This script has no rows to show yet.'}
                </p>
                <p className="text-faint text-[13px] mt-2">
                  {isBriefTemplate(script.template_key)
                    ? 'Switch on the Interview questions field in the Fields drawer and add a list under it.'
                    : 'Write some lines or visuals in the script rows and they will show here, one row at a time.'}
                </p>
              </div>
            ) : (
              <ol className="space-y-5">
                {questions.map((q, i) => (
                  <li
                    key={i}
                    ref={(el) => {
                      cardRefs.current[i] = el;
                    }}
                    onClick={() => handleJumpToQuestion(i)}
                    // Tapping a question while recording jumps the active
                    // marker — useful when the talent answers questions out
                    // of order.
                    className={isRecording ? 'cursor-pointer' : undefined}
                  >
                    <InterviewQuestionCard
                      index={i}
                      question={q}
                      active={isRecording && activeQuestion === i}
                    />
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {(recordError || recorder.error || downloadError) && (
          <div className="mt-6 px-4 py-3 border border-error/40 rounded-md bg-error/5 text-error text-[13px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] mb-1">
              {recordError || recorder.error ? 'Recording error' : 'PDF download error'}
            </div>
            {recordError || recorder.error || downloadError}
          </div>
        )}

        {lastUploadId && !isRecording && !uploading && (
          <div className="mt-6 px-4 py-3 border border-line rounded-md bg-card text-[13px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint mb-1">
              Take saved
            </div>
            <p className="text-muted">
              Uploaded. Tap Record to start the next take.
            </p>
          </div>
        )}

        {panel === 'questions' && (
          <p className="text-faint text-[12px] mt-12 text-center">
            {questions?.length ?? 0} question{questions?.length === 1 ? '' : 's'}
          </p>
        )}
      </section>

      {/* Sticky bottom action bar — the primary recording controls live
          here so they're always reachable on a phone without scrolling. */}
      {recorderOn && (
        <RecordingActionBar
          isRecording={isRecording}
          isStarting={isStarting}
          isStopping={isStopping}
          uploading={uploading}
          activeQuestion={activeQuestion}
          questionCount={questions.length}
          hasLastQuestion={hasLastQuestion}
          takeNumber={takeNumber}
          audioLevels={recorder.audioLevels}
          embedded={embedded}
          elapsedMs={recorder.elapsedMs}
          warnSoftly={recorder.warnSoftly}
          warnUrgently={recorder.warnUrgently}
          onStart={handleStartRecording}
          onStop={handleStopRecording}
          onNext={handleNextQuestion}
        />
      )}
    </main>
  );
}

// ---------- Helpers ----------

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function RecordingTimer({
  elapsedMs,
  takeNumber,
  warnSoftly,
  warnUrgently,
  audioLevels,
}: {
  elapsedMs: number;
  takeNumber: number;
  warnSoftly: boolean;
  warnUrgently: boolean;
  audioLevels: number[];
}) {
  const tone = warnUrgently
    ? 'text-error'
    : warnSoftly
      ? 'text-warning'
      : 'text-fg';
  return (
    <div className="flex items-center gap-2.5">
      <WaveformIndicator levels={audioLevels} active className="h-5" />
      <span className={`font-mono text-[14px] tabular-nums ${tone}`}>
        {formatElapsed(elapsedMs)}
      </span>
      <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        Take {takeNumber}
      </span>
    </div>
  );
}

function CountdownBanner({
  warnUrgently,
  elapsedMs,
}: {
  warnUrgently: boolean;
  elapsedMs: number;
}) {
  const remainingMs = Math.max(0, 29 * 60 * 1000 + 55 * 1000 - elapsedMs);
  const remainingS = Math.ceil(remainingMs / 1000);
  return (
    <div
      className={
        'border-t text-[12px] py-2 px-5 text-center ' +
        (warnUrgently
          ? 'border-error/40 bg-error/10 text-error'
          : 'border-warning/30 bg-warning/10 text-warning')
      }
    >
      {warnUrgently
        ? `Auto-stopping in ${remainingS}s — tap Stop to keep control`
        : '5 minutes remaining — tap Stop and Record again to extend'}
    </div>
  );
}

function RecordingActionBar({
  isRecording,
  isStarting,
  isStopping,
  uploading,
  activeQuestion,
  questionCount,
  hasLastQuestion,
  takeNumber,
  audioLevels,
  embedded,
  elapsedMs,
  warnSoftly,
  warnUrgently,
  onStart,
  onStop,
  onNext,
}: {
  isRecording: boolean;
  isStarting: boolean;
  isStopping: boolean;
  uploading: boolean;
  activeQuestion: number;
  questionCount: number;
  hasLastQuestion: boolean;
  takeNumber: number;
  audioLevels: number[];
  /** In the call-sheet frame the header (and its timer) is hidden, so the
   *  take's clock and countdown move down here. */
  embedded: boolean;
  elapsedMs: number;
  warnSoftly: boolean;
  warnUrgently: boolean;
  onStart: () => void;
  onStop: () => void;
  onNext: () => void;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 bg-page/95 backdrop-blur border-t border-line">
      {/* Live waveform strip at the very top of the bar — sits in the
          interviewer's eye-line whenever they reach for the Next-question
          button, so "is the mic actually hearing me?" never has to be a
          question. */}
      {embedded && isRecording && (warnSoftly || warnUrgently) && (
        <CountdownBanner warnUrgently={warnUrgently} elapsedMs={elapsedMs} />
      )}
      {isRecording && (
        <div className="border-b border-line/60 py-2 px-5 flex items-center justify-center gap-2">
          <WaveformIndicator
            levels={audioLevels}
            active
            className="h-5 flex-shrink-0"
          />
          {embedded && (
            <span
              className={`font-mono text-[12px] tabular-nums ${
                warnUrgently ? 'text-error' : warnSoftly ? 'text-warning' : 'text-fg'
              }`}
            >
              Take {takeNumber} · {formatElapsed(elapsedMs)}
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            {embedded ? '· Keep screen on' : 'Hearing you · keep screen on'}
          </span>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
        {!isRecording ? (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              {uploading
                ? 'Uploading take…'
                : isStarting
                  ? 'Starting mic…'
                  : `Take ${takeNumber} ready`}
            </p>
            <button
              onClick={onStart}
              disabled={isStarting || uploading}
              className="
                inline-flex items-center gap-2 rounded-full
                bg-accent text-white px-5 py-2.5
                font-mono text-[12px] uppercase tracking-[0.12em]
                hover:opacity-90 transition-opacity
                disabled:opacity-50
              "
            >
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-white" />
              Record
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onStop}
              disabled={isStopping}
              className="
                inline-flex items-center gap-2 rounded-full
                bg-card border border-line px-4 py-2.5
                font-mono text-[12px] uppercase tracking-[0.12em] text-fg
                hover:border-accent transition-colors
                disabled:opacity-50
              "
            >
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-accent" />
              {isStopping ? 'Stopping…' : 'Stop'}
            </button>
            <button
              onClick={onNext}
              disabled={hasLastQuestion}
              className="
                flex-1 ml-3 inline-flex items-center justify-center gap-2
                rounded-full bg-fg text-page px-5 py-3
                text-[14px] font-medium
                hover:opacity-90 transition-opacity
                disabled:opacity-40 disabled:cursor-not-allowed
              "
            >
              {hasLastQuestion
                ? `Q${activeQuestion + 1} of ${questionCount} — last`
                : `Next question (Q${activeQuestion + 2}) →`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
